import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { ListResult, StockCountListItem, StockCountStatus, WarehouseView } from "@cx/contracts";
import { formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { COUNT_STATUS_LABELS, COUNT_STATUS_TONES } from "./labels";

/**
 * Suprimentos › Conferência de estoque (RF-SUP-06): conferências por depósito (uma aberta por vez em cada um). A
 * contagem é feita lendo o código de barras; ao aplicar, as diferenças viram balanço.
 */
export function StockCountsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canCreate = can(perms, "stock", "create");
  const [status, setStatus] = useState<StockCountStatus | "">("");
  const [creating, setCreating] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ["stock-counts", companyId, "list", status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<StockCountListItem>>(
        `/stock/counts?${new URLSearchParams({
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <ContentHeader
        title="Conferência de estoque"
        breadcrumb={["Suprimentos", "Conferência de estoque"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Nova conferência
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="counts-status">
              Situação
            </label>
            <select
              id="counts-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as StockCountStatus | "")}
            >
              <option value="">Todas as situações</option>
              {(Object.keys(COUNT_STATUS_LABELS) as StockCountStatus[]).map((s) => (
                <option key={s} value={s}>
                  {COUNT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma conferência"
            description="Abra uma conferência no depósito e leia os códigos de barras dos produtos (leitor ou digitação). Ao aplicar, o saldo do depósito passa a ser o contado."
            actions={canCreate ? [{ label: "Nova conferência", onClick: () => setCreating(true) }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div
              className="table-responsive"
              tabIndex={0}
              role="region"
              aria-label="Conferências de estoque (rolagem)"
            >
              <table className="table align-middle mb-0" aria-label="Conferências de estoque">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Depósito</th>
                    <th scope="col">Aberta em</th>
                    <th scope="col" className="text-end">
                      Produtos lidos
                    </th>
                    <th scope="col" className="text-end">
                      Quantidade lida
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/suprimentos/conferencias/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>{r.warehouse.name}</td>
                      <td className="text-nowrap">{dateTime(r.createdAt)}</td>
                      <td className="text-end">{r.readProducts}</td>
                      <td className="text-end qty">{formatQty(r.readQ4)}</td>
                      <td>
                        <StatusBadge tone={COUNT_STATUS_TONES[r.status]}>
                          {COUNT_STATUS_LABELS[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {creating && <NewCountDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function NewCountDialog({ onClose }: { onClose(): void }) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  const active = (warehouses.data?.data ?? []).filter((w) => w.active);
  const [warehouseId, setWarehouseId] = useState("");
  const chosen = warehouseId || active.find((w) => w.isDefault)?.id || active[0]?.id || "";
  const [notes, setNotes] = useState("");
  const [idemKey] = useState(() => crypto.randomUUID());
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/stock/counts", {
        method: "POST",
        headers: { "Idempotency-Key": idemKey },
        body: JSON.stringify({ warehouseId: chosen, notes: notes.trim() || null }),
      }),
    onSuccess: (r) => navigate({ to: "/suprimentos/conferencias/$id", params: { id: r.id } }),
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title="Nova conferência de estoque"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!chosen || create.isPending}
            onClick={() => create.mutate()}
          >
            Abrir conferência
          </button>
        </>
      }
    >
      <QueryStatus loading={warehouses.isLoading} error={warehouses.error} />
      <Field label="Depósito" className="col-12">
        <select className="form-select" value={chosen} onChange={(e) => setWarehouseId(e.target.value)}>
          {active.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Observação" className="col-12">
        <input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}
